// §5 — Reconcile slide-over. Ported from FeeTable.jsx's ReconcileSheet. Opened
// from a fee row when the invoice disagrees with the fee, or to set the Credit
// Report's refresh-credit choice. Brings the invoice(s) and the decision together:
//   • Reconcile the fee to the marked-current invoices (auto-snaps to the last
//     disclosed amount when they match — autoReconcileTarget)
//   • Reduce the fee to the invoice total when the invoices come in short
//   • Escalate an over-disclosed / short difference to Closing with a stamped note
//   • Credit Report: pick "Includes a refresh" / "Refresh not required"
// All discrepancy / cost-to-cure / auto-reconcile math comes from doc-order-core
// (zero reimplementation). Rendered INLINE (fixed-position overlay, NO portal):
// the tab lives inside the Radix LoanDetailsSheet, whose modal lock sets
// pointer-events:none on document.body — a body portal lands outside the Radix
// content subtree and goes fully inert (Jennifer bugs 4/6). Inline keeps every
// event inside the dialog. The wrapper re-declares data-doc-order so the scoped
// styles apply. P1: NO upload affordances; "Mark Current" is a LIVE PREVIEW that
// persists markedCurrent via saveFee({ invoices }) only — it does NOT commit
// files to the eFolder, and the copy makes no such claim.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DocOrderMarkResult } from '@/services/docOrderService';
import { Icon } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtMoney, fmtStamp } from '../format';
import {
  activeFiledInvoices,
  autoReconcileTarget,
  costToCureCheck,
  invoiceHasDiscrepancy,
  invoiceTotal,
  isCurrentFeeEscalation,
  suggestInvoiceFolder,
  DO_TOLERANCE,
  type DocOrderFee,
  type RefreshCredit
} from '../core';
import { UploadPanel } from './UploadPanel';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ReconcileSheetProps {
  fee: DocOrderFee;
  onClose: () => void;
}

export function ReconcileSheet({ fee, onClose }: ReconcileSheetProps) {
  const { editable, role, userName, saveFee, saveFeeNow, escalateFee, undoEscalation, addToast, commitMarks, writebacks, invoiceFolderStatus, invoiceFolderDocs, checkUploadStatus, checkingUploads, busy } = useDocOrderCtx();
  const acks = fee.acks ?? {};

  const [note, setNote] = useState<string>(acks.escalation?.note ?? '');
  const [shortNote, setShortNote] = useState<string>(acks.shortfall?.note ?? '');
  // #8 — local input buffers for the amount fields. Bound directly to the fee
  // prop, they were re-rendered by the autosave echo on every keystroke (which
  // reset the caret and let the user type only ONE character). While focused the
  // buffer is authoritative and IGNORES the server echo; the value commits to
  // saveFee on blur, then the prop takes over again (buffer cleared to null).
  const [feeAmtBuf, setFeeAmtBuf] = useState<string | null>(null);
  const [invAmtBuf, setInvAmtBuf] = useState<Record<string, string>>({});
  // #15 — currency format: show 2 decimals when the field is idle (a number
  // input can't render trailing zeros, so these are text inputs). While focused
  // the raw buffer shows whatever the user is typing.
  const fmtAmt = (n: number | string | null | undefined) => (Number(n) || 0).toFixed(2);
  const sanitizeAmt = (s: string) => s.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  // Shared expand state for the inline invoice UploadPanel — opened from the
  // invoice-short resolution and from a failed upload's retry affordance.
  const [showUpload, setShowUpload] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  // R4 A7 — Mark-Current TRUTH. The eFolder commit (updateAttachment) fires on
  // Reconcile/Reduce/Escalate and is AWAITED, verified per attachment: the old
  // fire-and-forget reported success while every flip 403'd (live evidence:
  // five marks_committed events carrying `403 Forbidden, flipped:0`). Full
  // success closes the sheet; ANY failure keeps it open with per-file results
  // + a retry. The fee decision itself still saves either way (commitMarks
  // runs through runAction, which flushes the queued saveFee first) — the
  // copy distinguishes "fee decision saved" from "eFolder marks failed".
  const [committingMarks, setCommittingMarks] = useState(false);
  // R5 #7 decision-truth state (hoisted above handleClose — its dismissal
  // guard depends on both).
  const [deciding, setDeciding] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  // Round-11 #3: while a decision save or the AWAITED mark commit is in
  // flight, EVERY mutating control freezes — an amount/Mark-Current edit
  // flushed in that window could clear the reconcile server-side while the
  // sheet still closes with the success toast for the OLDER state.
  const frozen = deciding || committingMarks || busy || uploadBusy;
  const [markFailure, setMarkFailure] = useState<{ failures: DocOrderMarkResult[]; message: string } | null>(null);

  const runMarksCommit = async (): Promise<boolean> => {
    setCommittingMarks(true);
    const res = await commitMarks(fee.id);
    setCommittingMarks(false);
    // Rollback compatibility (R4 review): an OLDER edge returns marks WITHOUT
    // the typed results[] — its errors[] strings ("file: message") are still
    // the failure truth. Missing results must never read as an empty success.
    const results = res.marks?.results ?? null;
    const failures: DocOrderMarkResult[] = results
      ? results.filter((r) => !r.ok)
      : (res.marks?.errors ?? []).map((e, i) => {
          const sep = e.indexOf(': ');
          return {
            attachmentId: `legacy-${i}`,
            file: sep > 0 ? e.slice(0, sep) : e,
            markedCurrent: true,
            ok: false,
            error: sep > 0 ? e.slice(sep + 2) : 'update failed'
          };
        });
    if (res.ok && failures.length === 0) {
      setMarkFailure(null);
      return true;
    }
    const total = results ? results.length : failures.length;
    setMarkFailure({
      failures,
      message: res.ok
        ? `${failures.length} of ${total} eFolder mark${total === 1 ? '' : 's'} did NOT reach Encompass.`
        : `The eFolder marks call failed${res.error ? ` — ${res.error}` : ''}.`
    });
    return false;
  };

  const finishWithMarks = async (decisionToast: string, decisionIcon: string) => {
    addToast(decisionToast, decisionIcon);
    if (!writebacks.enabled) {
      onClose(); // write gate off — marks stay a local preview (unchanged)
      return;
    }
    if (await runMarksCommit()) {
      onClose();
    }
  };

  const retryMarks = async () => {
    if (await runMarksCommit()) {
      addToast('eFolder marks committed', 'check');
      onClose();
    }
  };

  const sheetRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // ONE close path for Escape, the backdrop scrim, AND the X (Batch 3.1 F3):
  // blur the focused input FIRST so its onBlur commits the amount buffer —
  // dismissing through the scrim/X previously discarded a typed-but-uncommitted
  // amount (only Escape had the blur). Stable identity so the keyboard effect
  // depends on it without re-binding per render.
  const handleClose = useCallback(() => {
    // Round-10 #2: X / scrim / Escape all route here — while a decision save
    // or the AWAITED mark commit is in flight, dismissal would unmount the
    // sheet the failure/retry UI renders into. Block until the result lands.
    if (frozen) {
      return;
    }
    (document.activeElement as HTMLElement | null)?.blur?.();
    onClose();
  }, [onClose, frozen]);

  // Focus management (WCAG 2.4.3 / dialog pattern): capture the opener, move
  // focus into the sheet, trap Tab within it, close on Escape, and restore focus
  // to the opener on unmount. The prototype only handled Escape.
  useEffect(() => {
    openerRef.current = document.activeElement as HTMLElement | null;
    const sheet = sheetRef.current;
    const focusable = () =>
      sheet ? Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null) : [];
    const initial = focusable();
    const target = initial.length > 0 ? initial[0] : sheet;
    target?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose(); // blur-then-close — commits a buffered amount edit
        return;
      }
      if (e.key !== 'Tab' || !sheet) {
return;
}
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        sheet.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      openerRef.current?.focus();
    };
  }, [handleClose]);

  const feeAmt = Number(fee.amount) || 0;
  const invoices = fee.invoices ?? [];
  // #9/#18 — the sheet's invoice evidence must match the fee ROW's (folder
  // mirror + upload entries), not raw fee.invoices alone; otherwise the row/
  // preview can show invoices while the sheet says "no invoice detected".
  const folderHasDocs = invoiceFolderStatus[fee.feeKey] ?? false;
  // eFolder-mirror docs behind this fee's dot (what the FeePeek preview shows) —
  // rendered as read-only rows when the upload state machine has no entries yet.
  const mirrorDocs = invoiceFolderDocs[fee.feeKey]?.docs ?? [];
  // Evidence split (Batch 3.1 F1): `hasInvoice` (ANY evidence or activity)
  // gates the docs display; the numeric COMPARE TABLE keys on FILED
  // marked-current invoices only — a queued/failed entry has no countable
  // amount, so "Invoices (0) $0 / Invoice short" must never render off it.
  // While an upload is QUEUED the resolution actions are withheld entirely
  // (a Reconcile fired on incomplete evidence 422s server-side); the
  // missing-captured-invoice escalation flow (bug 6) stays for failed/empty.
  const hasInvoiceEntries = invoices.length > 0;
  const hasInvoice = folderHasDocs || hasInvoiceEntries;
  const uploadPending = invoices.some((inv) => inv.uploadStatus === 'queued');
  const markedTotal = invoiceTotal(fee); // sum of FILED invoices still marked current
  const markedCount = activeFiledInvoices(fee).length;
  const hasFiledInvoices = markedCount > 0; // gates the numeric compare table
  const hasDiscrepancy = invoiceHasDiscrepancy(fee);
  // Two INDEPENDENT cost-to-cure tests + the marked delta, all from core.
  const ctc = costToCureCheck(fee);
  const { overDisclosed, costToCure, markedDelta } = ctc;
  const underFunded = !overDisclosed && markedDelta > DO_TOLERANCE.costToCure;
  const invoiceShort = !overDisclosed && markedDelta < -DO_TOLERANCE.costToCure;
  const shortAmount = invoiceShort ? -markedDelta : 0;
  const isRefreshFee = fee.refreshCredit !== undefined;
  const disclosed = fee.lastDisclosedLE;
  // Display-only overage (the boolean gate comes from ctc.invoicesOver).
  const invoiceOver = (disclosed !== null && disclosed !== undefined) ? markedTotal - disclosed : 0;
  const escalationAck = acks.escalation;
  // ONE kind-aware currency rule (R5) — shared with the server gate + FeeTable.
  const ackValid = isCurrentFeeEscalation(fee, escalationAck);
  const roleLabel = escalationAck ? (escalationAck.role === 'closing' ? 'Closing' : escalationAck.role === 'lo' ? 'LO' : 'Processor') : '';
  const defaultFolder = suggestInvoiceFolder(fee.name, fee);
  const fmtD = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtMoney(Math.abs(n), true)}`;

  const setRefresh = (v: RefreshCredit) => {
    if (!frozen) {
      saveFee(fee.id, { refreshCredit: v });
    }
  };
  const openUpload = () => {
    if (!frozen) {
      setShowUpload(true);
    }
  };
  // Mark Current is a live preview: persist the boolean on the fee's invoices —
  // no eFolder file-status write.
  // Round-11 #3: the three mutation emitters refuse outright while a decision
  // or mark commit is pending (the disabled attrs cover real browsers; these
  // make the contract hold even for synthetic events).
  const toggleMark = (uid: string, next: boolean) => {
    if (frozen) {
      return;
    }
    saveFee(fee.id, { invoices: invoices.map((inv) => (inv.uid === uid ? { ...inv, markedCurrent: next } : inv)) });
  };
  const setInvoiceAmount = (uid: string, amount: number) => {
    if (frozen) {
      return;
    }
    saveFee(fee.id, { invoices: invoices.map((inv) => (inv.uid === uid ? { ...inv, amount } : inv)) });
  };
  const setFeeAmount = (amount: number) => {
    if (frozen) {
      return;
    }
    saveFee(fee.id, { amount, reconciled: false });
  };

  // R5 #7 — DECISION TRUTH: reconcile/reduce are AWAITED saveFeeNow calls
  // whose patch FACTORIES compute from the CURRENT fee (post-flush,
  // re-evaluated after a conflict refetch — a pre-conflict financial patch is
  // never replayed). Escalate/undo are SERVER-OWNED typed actions (the kind
  // derives from stored state; generic patches can't touch escalations).
  // NOTHING toasts success, commits marks, or closes until the decision
  // PERSISTED; failures stay open with the server's reason inline.
  const reconcile = async () => {
    if (frozen) {
      return;
    }
    if (underFunded && !shortNote.trim()) {
      return;
    }
    setDeciding(true);
    setDecisionError(null);
    try {
      const shortNoteText = shortNote.trim();
      const out = await saveFeeNow(fee.id, (cur) => {
        // Factory: snap/deltas from the CURRENT fee, never the render-time one.
        const snap = autoReconcileTarget(cur);
        const curDelta = invoiceTotal(cur) - (Number(cur.amount) || 0);
        const curUnderFunded = curDelta > DO_TOLERANCE.costToCure;
        return {
          amount: snap ?? (Number(cur.amount) || 0),
          reconciled: true,
          acks: {
            ...(cur.acks ?? {}),
            shortfall: curUnderFunded && shortNoteText
              ? { note: shortNoteText, by: userName, role, at: new Date().toISOString(), delta: curDelta }
              : undefined
          }
        };
      });
      if (!out.ok) {
        setDecisionError(out.error || 'The reconcile was rejected — nothing was saved.');
        return;
      }
      await finishWithMarks('Fee reconciled', 'check');
    } finally {
      setDeciding(false);
    }
  };
  // Invoice-short resolution: accept the lower invoice by reducing the fee to
  // the marked-invoice total, then reconcile (lowering a disclosed fee never
  // needs re-disclosure).
  const reconcileAtInvoice = async () => {
    if (frozen) {
      return;
    }
    setDeciding(true);
    setDecisionError(null);
    try {
      let reducedTo = markedTotal;
      const out = await saveFeeNow(fee.id, (cur) => {
        reducedTo = invoiceTotal(cur);
        const nextAcks = { ...(cur.acks ?? {}) } as DocOrderFee['acks'];
        delete (nextAcks as Record<string, unknown>).shortfall;
        return { amount: reducedTo, reconciled: true, acks: nextAcks };
      });
      if (!out.ok) {
        setDecisionError(out.error || 'The reduce-and-reconcile was rejected — nothing was saved.');
        return;
      }
      await finishWithMarks(`Fee reduced to ${fmtMoney(reducedTo, true)} — reconciled`, 'check');
    } finally {
      setDeciding(false);
    }
  };
  const acknowledge = async () => {
    if (frozen) {
      return;
    }
    const n = note.trim();
    if (!n) {
      return;
    }
    setDeciding(true);
    setDecisionError(null);
    try {
      const res = await escalateFee(fee.id, n);
      if (!res.ok) {
        setDecisionError(res.error || 'The escalation was rejected — nothing was saved.');
        return;
      }
      // Toast from the SERVER-derived kind (Jennifer's copy for missing invoice).
      const kind = res.escalation?.kind;
      await finishWithMarks(
        kind === 'missing_invoice' ? 'Escalated for missing invoice' : 'Escalated to Closing for Cost to Cure',
        'flag'
      );
    } finally {
      setDeciding(false);
    }
  };
  const undo = async () => {
    if (frozen) {
      return;
    }
    setDeciding(true);
    setDecisionError(null);
    try {
      const res = await undoEscalation(fee.id);
      if (!res.ok) {
        setDecisionError(res.error || 'Could not remove the escalation.');
        return;
      }
      addToast('Escalation removed', 'rotateCcw');
    } finally {
      setDeciding(false);
    }
  };

  return (
    <div data-doc-order className="recon-overlay">
      <div className="recon-scrim" onClick={handleClose} />
      <aside ref={sheetRef} className="recon-sheet" role="dialog" aria-modal="true" aria-label={`Reconcile ${fee.name}`} tabIndex={-1}>
        <header className="recon-hd">
          <div>
            <div className="recon-eyebrow">
              <Icon name="scale" size={12} />
              {isRefreshFee && !hasDiscrepancy ? 'Reconcile credit' : 'Reconcile invoice'}
            </div>
            <div className="recon-title">
              {fee.name}
              {fee.payee ? <span className="recon-payee"> · {fee.payee}</span> : null}
            </div>
          </div>
          <button className="recon-close" onClick={handleClose} aria-label="Close" disabled={deciding || committingMarks}>
            <Icon name="x" size={15} />
          </button>
        </header>

        {decisionError && (
          <div className="recon-marks-fail" role="alert">
            <div className="recon-marks-fail-hd">
              <Icon name="alertTriangle" size={14} />
              Nothing was saved
            </div>
            <div className="recon-marks-fail-msg">{decisionError}</div>
          </div>
        )}
        {markFailure && (
          <div className="recon-marks-fail" role="alert">
            <div className="recon-marks-fail-hd">
              <Icon name="alertTriangle" size={14} />
              Fee decision saved — eFolder marks failed
            </div>
            <div className="recon-marks-fail-msg">{markFailure.message}</div>
            {markFailure.failures.map((f) => (
              <div key={f.attachmentId} className="recon-marks-fail-row">
                <span className="recon-marks-fail-file">{f.file ?? f.attachmentId}</span>
                <span className="recon-marks-fail-err">{f.error ?? 'update failed'}</span>
              </div>
            ))}
            <button type="button" className="recon-act ghost" disabled={frozen} onClick={() => void retryMarks()}>
              <Icon name="refresh" size={13} />
              {committingMarks ? 'Retrying…' : 'Retry eFolder marks'}
            </button>
          </div>
        )}

        {isRefreshFee && (
          <div className="recon-refresh recon-refresh-top">
            <div className="recon-refresh-q">
              Credit refresh <span className="req">*</span> — required before submit.
            </div>
            <div className="recon-choices">
              <button
                className={`recon-choice ${fee.refreshCredit === 'included' ? 'sel' : (fee.refreshCredit === null || fee.refreshCredit === undefined) ? 'unset' : ''}`}
                disabled={!editable || frozen}
                onClick={() => setRefresh('included')}
              >
                <span className="recon-choice-mark">
                  <Icon name="check" size={12} />
                </span>
                <span>
                  <b>Includes a refresh</b>
                  <span className="recon-choice-sub">A credit re-pull / refresh is bundled into this fee.</span>
                </span>
              </button>
              <button
                className={`recon-choice ${fee.refreshCredit === 'not-required' ? 'sel' : (fee.refreshCredit === null || fee.refreshCredit === undefined) ? 'unset' : ''}`}
                disabled={!editable || frozen}
                onClick={() => setRefresh('not-required')}
              >
                <span className="recon-choice-mark">
                  <Icon name="check" size={12} />
                </span>
                <span>
                  <b>Refresh not required</b>
                  <span className="recon-choice-sub">No credit refresh is needed for this file.</span>
                </span>
              </button>
            </div>
          </div>
        )}

        {hasInvoice ? (
          <div className="recon-docs">
            {invoices.map((inv) => {
              // Filed ≠ synced: a 'queued' upload is enqueued but not in the
              // eFolder yet, and 'failed' needs a retry — neither counts toward
              // the reconcile total (activeFiledInvoices), so we hold back the
              // amount-edit + Mark-Current controls until the entry is filed.
              const isQueued = inv.uploadStatus === 'queued';
              const isFailed = inv.uploadStatus === 'failed';
              const filed = !isQueued && !isFailed;
              return (
                <div className={`recon-doc ${isQueued ? 'is-queued' : ''} ${isFailed ? 'is-failed' : ''}`.trim()} key={inv.uid}>
                  <div className="recon-doc-thumb">
                    <Icon name="fileText" size={22} />
                  </div>
                  <div className="recon-doc-meta">
                    <div className="recon-doc-name">{inv.file || 'Invoice'}</div>
                    {/* Truthful status copy (Batch 3.1): only a FILED/legacy
                        entry may claim eFolder presence + a counted amount.
                        Round 3: ADOPTED entries (pre-existing eFolder docs) have
                        no AI read — the processor types the amount. */}
                    <div className="recon-doc-sub">
                      {inv.folder || defaultFolder}
                      {isQueued
                        ? ' · uploading — not in the eFolder yet'
                        : isFailed
                          ? ' · not filed'
                          : inv.adopted
                            ? ' · eFolder'
                            : ' · eFolder · AI-read amount'}
                    </div>
                    {isQueued && (
                      <div className="do-up-pill queued">
                        <Icon name="clock" size={10} />
                        {/* #5: a held upload (loan locked in Encompass) carries a
                            reason on the still-queued entry — show it so the pill
                            never spins on a bare "Uploading…" with no explanation. */}
                        {inv.uploadError || 'Uploading to Encompass…'}
                      </div>
                    )}
                    {isFailed && (
                      <div className="do-up-pill failed">
                        <Icon name="alertTriangle" size={10} />
                        {inv.uploadError || 'Upload failed'}
                        {editable && (
                          <button type="button" className="do-up-retry" disabled={frozen} onClick={openUpload}>
                            Retry
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* AI-extracted amount — trusted but EDITABLE: the processor can
                      correct a misread; the corrected figure feeds the discrepancy /
                      cost-to-cure / auto-reconcile math. R3.1 #4: ADOPTED entries
                      (pre-existing eFolder docs) have no AI read — the tooltip must
                      not claim one. */}
                  {filed &&
                    (editable ? (
                      <span
                        className="recon-amt-edit"
                        title={
                          inv.adopted
                            ? 'Invoice amount — type it from the document'
                            : 'Amount read off the invoice by AI — edit if the read is wrong'
                        }
                      >
                        <span className="recon-amt-prefix">$</span>
                        <input
                          className="recon-amt-inp"
                          type="text"
                          inputMode="decimal"
                          disabled={frozen}
                          value={invAmtBuf[inv.uid] ?? fmtAmt(inv.amount)}
                          onFocus={() => {
                            if (!frozen) {
                              setInvAmtBuf((b) => ({ ...b, [inv.uid]: fmtAmt(inv.amount) }));
                            }
                          }}
                          onChange={(e) => {
                            if (!frozen) {
                              setInvAmtBuf((b) => ({ ...b, [inv.uid]: sanitizeAmt(e.target.value) }));
                            }
                          }}
                          onBlur={() => {
                            if (frozen) {
                              setInvAmtBuf((b) => {
                                const next = { ...b };
                                delete next[inv.uid];
                                return next;
                              });
                              return; // round-11 #3: no commits mid-decision/marks
                            }
                            const raw = invAmtBuf[inv.uid];
                            if (raw === undefined) {
                              return; // untouched — keep the prop value
                            }
                            // R5 #7a: the buffer seeds on FOCUS, so a
                            // close-blur commits an UNCHANGED value — that
                            // {invoices} save made the server clear a
                            // just-saved reconcile (rev 38→39 revert). A
                            // numeric no-op never emits a save.
                            if ((Number(raw) || 0) !== (Number(inv.amount) || 0)) {
                              setInvoiceAmount(inv.uid, Number(raw) || 0);
                            }
                            setInvAmtBuf((b) => {
                              const next = { ...b };
                              delete next[inv.uid];
                              return next;
                            });
                          }}
                        />
                      </span>
                    ) : (
                      <div className="recon-doc-amt">{fmtMoney(Number(inv.amount) || 0, true)}</div>
                    ))}
                  {filed && (
                    <label className="recon-mark" title="Mark Current — uncheck to exclude this file from the reconcile total">
                      <input
                        type="checkbox"
                        checked={inv.markedCurrent !== false}
                        disabled={!editable || frozen}
                        onChange={(e) => toggleMark(inv.uid, e.target.checked)}
                      />
                      <span>Mark Current</span>
                    </label>
                  )}
                </div>
              );
            })}
            {/* #9 (Batch 3.1 F1): eFolder mirror docs ALWAYS render alongside
                the upload entries — a queued/failed entry must never hide real
                pre-existing evidence. Dedupe by ATTACHMENT IDENTITY only, and
                only against FILED/legacy entries (which alone carry an
                attachmentId); NEVER by filename — two distinct files legitimately
                share names (the round-26 DIC incident). */}
            {mirrorDocs
              .filter(
                (d) =>
                  !d.attachmentId ||
                  !invoices.some(
                    (inv) =>
                      (inv.uploadStatus === undefined || inv.uploadStatus === 'filed') &&
                      inv.attachmentId &&
                      inv.attachmentId === d.attachmentId
                  )
              )
              .map((d, di) => (
                <div className="recon-doc" key={`mirror-${d.attachmentId ?? d.file}-${di}`}>
                  <div className="recon-doc-thumb">
                    <Icon name="fileText" size={22} />
                  </div>
                  <div className="recon-doc-meta">
                    <div className="recon-doc-name">{d.file || 'Invoice'}</div>
                    <div className="recon-doc-sub">{invoiceFolderDocs[fee.feeKey]?.folder || defaultFolder} · eFolder</div>
                  </div>
                </div>
              ))}
            {editable && showUpload && !invoiceShort && (
              <div className="recon-upload">
                <UploadPanel
                  mode="invoice"
                  fee={fee}
                  disabled={frozen}
                  onPendingChange={setUploadBusy}
                  onClose={() => setShowUpload(false)}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="recon-empty">
            <Icon name="alertCircle" size={13} />
            No invoice on file yet.
          </div>
        )}

        {hasFiledInvoices && (
          <div className="recon-compare">
            <div className="recon-cmp-row">
              <span>Actual Fee for Loan Docs / Itemization</span>
              <span className="recon-cmp-val">
                {editable ? (
                  <span className="recon-amt-edit">
                    <span className="recon-amt-prefix">$</span>
                    <input
                      className="recon-amt-inp"
                      type="text"
                      inputMode="decimal"
                      disabled={frozen}
                      value={feeAmtBuf ?? fmtAmt(fee.amount)}
                      onFocus={() => {
                        if (!frozen) {
                          setFeeAmtBuf(fmtAmt(fee.amount));
                        }
                      }}
                      onChange={(e) => {
                        if (!frozen) {
                          setFeeAmtBuf(sanitizeAmt(e.target.value));
                        }
                      }}
                      onBlur={() => {
                        if (frozen) {
                          setFeeAmtBuf(null);
                          return; // round-11 #3
                        }
                        if (feeAmtBuf === null) {
                          return; // untouched
                        }
                        // R5 #7a: same no-op guard — an unchanged Actual-Fee
                        // buffer must not emit {amount, reconciled:false}.
                        if ((Number(feeAmtBuf) || 0) !== (Number(fee.amount) || 0)) {
                          setFeeAmount(Number(feeAmtBuf) || 0);
                        }
                        setFeeAmtBuf(null);
                      }}
                    />
                  </span>
                ) : (
                  <b>{fmtMoney(feeAmt, true)}</b>
                )}
              </span>
            </div>
            <div className="recon-cmp-row">
              <span>
                Last disclosed fee <span className="recon-cmp-src">LE / GFE</span>
              </span>
              <b>{(disclosed !== null && disclosed !== undefined) ? fmtMoney(disclosed, true) : '—'}</b>
            </div>
            <div className="recon-cmp-row">
              <span>
                Invoice{markedCount === 1 ? '' : 's'} ({markedCount})
              </span>
              <span className="recon-cmp-val">
                <b>{fmtMoney(markedTotal, true)}</b>
                {ctc.invoicesOver && <span className="recon-over-tag">{fmtD(invoiceOver)} under disclosed</span>}
              </span>
            </div>
            {overDisclosed ? (
              <div className="recon-cmp-row delta over">
                <span>Cost to Cure</span>
                <b>{fmtD(costToCure)}</b>
              </div>
            ) : invoiceShort ? (
              <div className="recon-cmp-row delta under">
                <span>Invoice short</span>
                <b>Need {fmtMoney(shortAmount, true)}</b>
              </div>
            ) : (
              <div className={`recon-cmp-row delta ${Math.abs(markedDelta) < DO_TOLERANCE.costToCure ? 'ok' : 'under'}`}>
                <span>Difference</span>
                <b>{Math.abs(markedDelta) < DO_TOLERANCE.costToCure ? 'Matches' : `${fmtD(markedDelta)} under`}</b>
              </div>
            )}
          </div>
        )}

        {editable ? (
          uploadPending ? (
            // Batch 3.1 F1 (blocker): while an upload is QUEUED the evidence is
            // incomplete — a Reconcile/Escalate fired now would run on numbers
            // that change the moment the file lands (and a no-invoice reconcile
            // 422s server-side). Hold the actions and offer the lightweight
            // status check instead; the entry files itself once the queue (or a
            // released loan lock) lets it through.
            <div className="recon-waiting">
              <Icon name="clock" size={13} />
              <span>Waiting for the upload to file to Encompass — reconcile actions unlock when it lands.</span>
              <button
                type="button"
                className="recon-act ghost"
                disabled={checkingUploads || frozen}
                onClick={() => {
                  if (!frozen) {
                    void checkUploadStatus();
                  }
                }}
              >
                <Icon name="refresh" size={13} />
                {checkingUploads ? 'Checking…' : 'Check status'}
              </button>
            </div>
          ) : ackValid ? (
            <div className="recon-ack">
              <div className="recon-ack-stamp">
                <Icon name="flag" size={13} />
                Escalated to Closing by <b>{escalationAck?.by}</b> ({roleLabel}) · {fmtStamp(escalationAck?.at)}
              </div>
              <div className="recon-ack-note">“{escalationAck?.note}”</div>
              <button className="recon-act ghost" disabled={frozen} onClick={() => void undo()}>
                <Icon name="rotateCcw" size={13} />
                Undo escalation
              </button>
            </div>
          ) : (
            <>
              {!invoiceShort && (
                <button
                  className="recon-act primary"
                  disabled={overDisclosed || (underFunded && !shortNote.trim()) || frozen}
                  onClick={() => void reconcile()}
                  title={
                    overDisclosed
                      ? 'Fee or invoices exceed the last disclosed fee — escalate to Cost to Cure'
                      : underFunded && !shortNote.trim()
                        ? 'Explain how the additional fee is paid before reconciling'
                        : undefined
                  }
                >
                  <Icon name="check" size={15} />
                  Reconcile
                </button>
              )}
              {!overDisclosed && invoiceShort && (
                <>
                  {/* #3: only offer "reduce to invoice" when there's a filed
                      invoice to reduce TO — never a destructive $0.00 reconcile. */}
                  {markedTotal > 0 && (
                    <button className="recon-act primary" disabled={frozen} onClick={() => void reconcileAtInvoice()}>
                      <Icon name="check" size={15} />
                      Reduce fee to invoice ({fmtMoney(markedTotal, true)}) &amp; reconcile
                    </button>
                  )}
                  <div className="recon-or">
                    <span>
                      Invoice(s) are {fmtMoney(shortAmount, true)} short of the fee being collected. Reduce the fee to the invoice above, upload
                      another invoice for the difference, or add a note below if there is no invoice for this fee.
                    </span>
                  </div>
                  {showUpload ? (
                    <div className="recon-upload">
                      <UploadPanel
                        mode="invoice"
                        fee={fee}
                        disabled={frozen}
                        onPendingChange={setUploadBusy}
                        onClose={() => setShowUpload(false)}
                      />
                    </div>
                  ) : (
                    <button className="recon-act ghost" disabled={frozen} onClick={openUpload}>
                      <Icon name="upload" size={15} />
                      Upload Invoice
                    </button>
                  )}
                  <label className="recon-lbl">
                    Note for closing regarding missing invoice <span className="req">*</span>
                  </label>
                  <textarea
                    className="recon-note"
                    rows={2}
                    value={note}
                    disabled={frozen}
                    placeholder="e.g. Awaiting a corrected invoice from the appraiser — escalate the short amount for closing review."
                    onChange={(e) => {
                      if (!frozen) {
                        setNote(e.target.value);
                      }
                    }}
                  />
                  <button className="recon-act gold" disabled={!note.trim() || frozen} onClick={() => void acknowledge()}>
                    <Icon name="flag" size={15} />
                    Escalate to Closing
                  </button>
                </>
              )}
              {!overDisclosed && underFunded && (
                <>
                  <div className="recon-or">
                    <span>Fee entered is less than the marked invoices — explain how the additional {fmtD(markedDelta)} is paid</span>
                  </div>
                  <label className="recon-lbl">
                    How is the additional {fmtMoney(markedDelta, true)} being paid? <span className="req">*</span>
                  </label>
                  <textarea
                    className="recon-note"
                    rows={2}
                    value={shortNote}
                    disabled={frozen}
                    placeholder="e.g. Borrower to pay the difference at closing as POC; or LO crediting the overage."
                    onChange={(e) => {
                      if (!frozen) {
                        setShortNote(e.target.value);
                      }
                    }}
                  />
                </>
              )}
              {overDisclosed && (
                <>
                  <div className="recon-or">
                    <span>
                      {ctc.amountOver && ctc.invoicesOver
                        ? 'Fee and invoices exceed'
                        : ctc.amountOver
                          ? 'Fee entered exceeds'
                          : 'Invoices exceed'}{' '}
                      the last disclosed fee. A Cost to Cure may be required.
                    </span>
                  </div>
                  {escalationAck && !ackValid && (
                    <div className="recon-stale">
                      <Icon name="alertCircle" size={12} />
                      Amounts changed since {escalationAck.by} escalated a {fmtD(Number(escalationAck.delta) || 0)} difference — please
                      re-confirm.
                    </div>
                  )}
                  <label className="recon-lbl">
                    Note for Closing — Cost to Cure <span className="req">*</span>
                  </label>
                  <textarea
                    className="recon-note"
                    rows={2}
                    value={note}
                    disabled={frozen}
                    placeholder="e.g. CDA billed a bundled field-review add-on; flag for Cost to Cure analysis at closing."
                    onChange={(e) => {
                      if (!frozen) {
                        setNote(e.target.value);
                      }
                    }}
                  />
                  <button className="recon-act gold" disabled={!note.trim() || frozen} onClick={() => void acknowledge()}>
                    <Icon name="flag" size={15} />
                    Escalate to Closing for Cost to Cure analysis
                  </button>
                </>
              )}
            </>
          )
        ) : hasDiscrepancy ? (
          <div className="recon-empty">The Processor can reconcile or escalate the difference to Closing.</div>
        ) : hasInvoice ? (
          <div className="recon-match-ok">
            <Icon name="checkCircle" size={13} />
            Invoice matches the fee.
          </div>
        ) : null}

        <div className="recon-stamp">
          Escalations are stamped to {userName} and auto-expire if the amount changes. Flagged fees carry to the Closing form for Cost to Cure
          analysis.
        </div>
      </aside>
    </div>
  );
}
